import { useState, useEffect } from "react";
import { 
  Settings as SettingsIcon, 
  Save, 
  TestTube, 
  Server,
  Key,
  Clock,
  ToggleLeft,
  AlertTriangle,
  CheckCircle
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import api from "../services/api";
import { toast } from "sonner";

export default function Settings() {
  const [settings, setSettings] = useState({
    rodopeso_base_url: "",
    rodopeso_api_key: "",
    rodopeso_device_key: "",
    rodopeso_branch: 1,
    senior_base_url: "",
    senior_username: "",
    senior_password: "",
    polling_interval_seconds: 5,
    detect_new_weighings_only: true,
    environment: "development"
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingRodopeso, setTestingRodopeso] = useState(false);
  const [testingSenior, setTestingSenior] = useState(false);
  const [testResults, setTestResults] = useState({ rodopeso: null, senior: null });

  const fetchSettings = async () => {
    try {
      const res = await api.get("/settings");
      setSettings(prev => ({ ...prev, ...res.data }));
    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Don't send empty password
      const dataToSend = { ...settings };
      if (!dataToSend.senior_password) {
        delete dataToSend.senior_password;
      }
      delete dataToSend.senior_password_set;
      delete dataToSend.environment;
      
      await api.put("/settings", dataToSend);
      toast.success("Configurações salvas com sucesso");
    } catch (error) {
      toast.error("Erro ao salvar configurações");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleEnvironment = async () => {
    try {
      const res = await api.patch("/settings/environment");
      setSettings(prev => ({ ...prev, environment: res.data.environment }));
      toast.success(`Ambiente alterado para: ${res.data.environment.toUpperCase()}`);
    } catch (error) {
      toast.error("Erro ao alternar ambiente");
    }
  };

  const testRodopeso = async () => {
    setTestingRodopeso(true);
    setTestResults(prev => ({ ...prev, rodopeso: null }));
    try {
      const res = await api.post("/test-connection/rodopeso");
      setTestResults(prev => ({ ...prev, rodopeso: res.data }));
      if (res.data.success) {
        toast.success("Conexão com RODOPESO OK!");
      } else {
        toast.error(`Falha: ${res.data.message}`);
      }
    } catch (error) {
      setTestResults(prev => ({ ...prev, rodopeso: { success: false, message: error.message } }));
      toast.error("Erro ao testar conexão");
    } finally {
      setTestingRodopeso(false);
    }
  };

  const testSenior = async () => {
    setTestingSenior(true);
    setTestResults(prev => ({ ...prev, senior: null }));
    try {
      const res = await api.post("/test-connection/senior");
      setTestResults(prev => ({ ...prev, senior: res.data }));
      if (res.data.success) {
        toast.success("Conexão com SENIOR OK!");
      } else {
        toast.error(`Falha: ${res.data.message}`);
      }
    } catch (error) {
      setTestResults(prev => ({ ...prev, senior: { success: false, message: error.message } }));
      toast.error("Erro ao testar conexão");
    } finally {
      setTestingSenior(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <SettingsIcon className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-in" data-testid="settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white font-mono">Configurações</h1>
          <p className="text-sm text-zinc-500">Configure as credenciais e parâmetros do gateway</p>
        </div>
        <Button 
          onClick={handleSave}
          disabled={saving}
          className="gap-2 bg-cyan-500 hover:bg-cyan-600 text-black font-medium btn-glow"
          data-testid="save-settings-btn"
        >
          <Save className="w-4 h-4" />
          {saving ? "Salvando..." : "Salvar Configurações"}
        </Button>
      </div>

      {/* Environment Toggle */}
      <Card className={`${settings.environment === 'production' ? 'bg-red-500/5 border-red-500/30' : 'bg-zinc-900/50 border-zinc-800'}`}>
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {settings.environment === 'production' ? (
                <AlertTriangle className="w-5 h-5 text-red-400" />
              ) : (
                <ToggleLeft className="w-5 h-5 text-cyan-400" />
              )}
              <div>
                <p className="text-sm font-medium text-white">Ambiente Atual</p>
                <p className="text-xs text-zinc-500">
                  {settings.environment === 'production' 
                    ? 'Cuidado! Todas as operações serão realizadas em produção.' 
                    : 'Ambiente seguro para testes e desenvolvimento.'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Badge className={settings.environment === 'production' ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'}>
                {settings.environment.toUpperCase()}
              </Badge>
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleToggleEnvironment}
                data-testid="toggle-environment-btn"
              >
                Alternar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* RODOPESO Settings */}
        <Card className="bg-zinc-900/50 border-zinc-800" data-testid="rodopeso-settings-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Server className="w-5 h-5 text-cyan-400" />
                <CardTitle className="font-mono text-white">RODOPESO</CardTitle>
              </div>
              <Badge className="badge-rest">REST API</Badge>
            </div>
            <CardDescription>Configurações de conexão com o sistema de pesagem</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="rodopeso_base_url">URL Base</Label>
              <Input
                id="rodopeso_base_url"
                value={settings.rodopeso_base_url || ""}
                onChange={(e) => setSettings({ ...settings, rodopeso_base_url: e.target.value })}
                placeholder="https://rodopeso.exemplo.com"
                className="bg-zinc-800 border-zinc-700 font-mono text-sm"
                data-testid="rodopeso-url-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="rodopeso_api_key">Chave GIA (sCdGiaBancoDados)</Label>
              <Input
                id="rodopeso_api_key"
                value={settings.rodopeso_api_key || ""}
                onChange={(e) => setSettings({ ...settings, rodopeso_api_key: e.target.value })}
                placeholder="5A5F34DDE5C14168A3C07612ECC7CC22"
                className="bg-zinc-800 border-zinc-700 font-mono text-sm"
                data-testid="rodopeso-api-key-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="rodopeso_device_key">Chave Dispositivo (sCdDispositivo)</Label>
              <Input
                id="rodopeso_device_key"
                value={settings.rodopeso_device_key || ""}
                onChange={(e) => setSettings({ ...settings, rodopeso_device_key: e.target.value })}
                placeholder="14386905FF5247B4B9B1F09512F10779"
                className="bg-zinc-800 border-zinc-700 font-mono text-sm"
                data-testid="rodopeso-device-key-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="rodopeso_branch">Filial (nCdFilial)</Label>
              <Input
                id="rodopeso_branch"
                type="number"
                value={settings.rodopeso_branch || 1}
                onChange={(e) => setSettings({ ...settings, rodopeso_branch: parseInt(e.target.value) })}
                className="bg-zinc-800 border-zinc-700 font-mono text-sm"
                data-testid="rodopeso-branch-input"
              />
            </div>

            <Separator className="bg-zinc-800" />

            <div className="flex items-center justify-between">
              <Button 
                variant="outline" 
                onClick={testRodopeso}
                disabled={testingRodopeso}
                className="gap-2"
                data-testid="test-rodopeso-btn"
              >
                <TestTube className="w-4 h-4" />
                {testingRodopeso ? "Testando..." : "Testar Conexão"}
              </Button>
              {testResults.rodopeso && (
                <div className={`flex items-center gap-2 ${testResults.rodopeso.success ? 'text-green-400' : 'text-red-400'}`}>
                  {testResults.rodopeso.success ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    <AlertTriangle className="w-4 h-4" />
                  )}
                  <span className="text-sm">{testResults.rodopeso.success ? 'OK' : testResults.rodopeso.message}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* SENIOR Settings */}
        <Card className="bg-zinc-900/50 border-zinc-800" data-testid="senior-settings-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Server className="w-5 h-5 text-orange-400" />
                <CardTitle className="font-mono text-white">SENIOR ERP</CardTitle>
              </div>
              <Badge className="badge-soap">SOAP WebService</Badge>
            </div>
            <CardDescription>Configurações de conexão com o ERP</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="senior_base_url">URL WebService</Label>
              <Input
                id="senior_base_url"
                value={settings.senior_base_url || ""}
                onChange={(e) => setSettings({ ...settings, senior_base_url: e.target.value })}
                placeholder="https://ocweb01s2h.seniorcloud.com.br:30451/g5-senior-services/..."
                className="bg-zinc-800 border-zinc-700 font-mono text-sm"
                data-testid="senior-url-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="senior_username">Usuário</Label>
              <Input
                id="senior_username"
                value={settings.senior_username || ""}
                onChange={(e) => setSettings({ ...settings, senior_username: e.target.value })}
                placeholder="usuario_integracao"
                className="bg-zinc-800 border-zinc-700"
                data-testid="senior-username-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="senior_password">Senha</Label>
              <Input
                id="senior_password"
                type="password"
                value={settings.senior_password || ""}
                onChange={(e) => setSettings({ ...settings, senior_password: e.target.value })}
                placeholder={settings.senior_password_set ? "••••••••" : "Digite a senha"}
                className="bg-zinc-800 border-zinc-700"
                data-testid="senior-password-input"
              />
              {settings.senior_password_set && !settings.senior_password && (
                <p className="text-xs text-zinc-500">Deixe em branco para manter a senha atual</p>
              )}
            </div>

            <Separator className="bg-zinc-800" />

            <div className="flex items-center justify-between">
              <Button 
                variant="outline" 
                onClick={testSenior}
                disabled={testingSenior}
                className="gap-2"
                data-testid="test-senior-btn"
              >
                <TestTube className="w-4 h-4" />
                {testingSenior ? "Testando..." : "Testar Conexão"}
              </Button>
              {testResults.senior && (
                <div className={`flex items-center gap-2 ${testResults.senior.success ? 'text-green-400' : 'text-red-400'}`}>
                  {testResults.senior.success ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    <AlertTriangle className="w-4 h-4" />
                  )}
                  <span className="text-sm">{testResults.senior.success ? 'OK' : testResults.senior.message}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Gateway Settings */}
      <Card className="bg-zinc-900/50 border-zinc-800" data-testid="gateway-settings-card">
        <CardHeader>
          <div className="flex items-center gap-2">
            <SettingsIcon className="w-5 h-5 text-cyan-400" />
            <CardTitle className="font-mono text-white">Configurações do Gateway</CardTitle>
          </div>
          <CardDescription>Parâmetros de comportamento do gateway</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="polling_interval">Intervalo de Polling (segundos)</Label>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-zinc-500" />
                <Input
                  id="polling_interval"
                  type="number"
                  min="1"
                  max="60"
                  value={settings.polling_interval_seconds || 5}
                  onChange={(e) => setSettings({ ...settings, polling_interval_seconds: parseInt(e.target.value) })}
                  className="bg-zinc-800 border-zinc-700 w-24"
                  data-testid="polling-interval-input"
                />
                <span className="text-sm text-zinc-500">segundos</span>
              </div>
              <p className="text-xs text-zinc-500">Frequência de verificação de novas pesagens</p>
            </div>

            <div className="space-y-2">
              <Label>Detecção Inteligente</Label>
              <div className="flex items-center gap-3 p-3 bg-zinc-800/50 rounded-sm">
                <Switch 
                  id="detect_new_only"
                  checked={settings.detect_new_weighings_only}
                  onCheckedChange={(v) => setSettings({ ...settings, detect_new_weighings_only: v })}
                  data-testid="detect-new-only-switch"
                />
                <div>
                  <Label htmlFor="detect_new_only" className="text-white cursor-pointer">
                    Apenas novas pesagens
                  </Label>
                  <p className="text-xs text-zinc-500">
                    Envia para o ERP somente quando detectar alterações na lista de pesagens
                  </p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Webhook Info */}
      <Card className="bg-cyan-500/5 border-cyan-500/20" data-testid="webhook-info-card">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Key className="w-5 h-5 text-cyan-400 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-cyan-400">Endpoint do Webhook</p>
              <p className="text-xs text-zinc-400 mt-1">
                O RODOPESO deve enviar as pesagens para o seguinte endpoint:
              </p>
              <code className="block mt-2 p-2 bg-zinc-900 rounded text-xs font-mono text-cyan-300">
                POST {window.location.origin}/api/gateway/webhook/rodopeso
              </code>
              <p className="text-xs text-zinc-500 mt-2">
                Inclua o header <code className="text-cyan-400">X-API-Key</code> com a chave de API para autenticação.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
